import { EventEmitter, Injectable, OnDestroy } from '@angular/core';
import { ProjectCommunicationService } from '../../communication/project-comms.service';
import { ProjectService } from '../../project/project.service';
import { take, takeUntil } from 'rxjs/operators';
import { BehaviorSubject } from 'rxjs';
import { ScenarioComparison } from '../../models/results/scenario-comparison/scenario-comparison.model';
import { Scenario } from '../../models/results/scenario-comparison/scenario.model';
import { AnessaTranslationService } from '../../helper/translation/anessa-translation.service';
import { ScenarioInput } from '../../models/results/scenario-comparison/scenario-input.model';
import Utils from '../../util';
import { CommunicationService } from '../../communication/communication.service';
import { ProjectInputItem } from '../../models/data-structures/project/project-input-item.model';
import { ConfiguredToastrService } from '../../helper/toastr/configured-toastr.service';
import { BiogasShare } from '../../models/project/plant-specifications/biogas-share.model';
import { CustomSnackbarComponent } from '../../helper/custom-snackbar/custom-snackbar.component';
import { MatSnackBar } from '@angular/material/snack-bar';
import { AffectedScenarioInput } from '../../models/results/scenario-comparison/affected-scenario-input.model';

@Injectable({
  providedIn: 'root'
})
export class ScenarioComparisonService implements OnDestroy {
  // used to wrap up subscriptions on destroy; service must be provided in a component
  // for it to be destroyed, it's currently provided in the scenario comparison component
  onDestroyNotifier = new EventEmitter();

  // notifies when this service is ready
  serviceReadyNotifier = new BehaviorSubject(false);
  // emits when this service has been updated
  updateNotifier = new EventEmitter();

  // true when project comms service is ready to be used
  private projectCommServiceReady = false;
  // true when project service is ready to be used
  private projectServiceReady = false;

  // the current scenario comparison object. changes and updates are made to this object
  private scenarioComparison?: ScenarioComparison;

  /**
   * Provides injections used within the service
   * @param projectCommService
   * @param projectService
   * @param translationService
   * @param commService
   * @param toastrService
   * @param snackService
   */
  constructor(private projectCommService: ProjectCommunicationService,
              private projectService: ProjectService,
              private translationService: AnessaTranslationService,
              private commService: CommunicationService,
              private toastrService: ConfiguredToastrService,
              private snackService: MatSnackBar
  ) {
    // when project comm service is loaded update the loaded state and set the current scenario comparison
    this.projectCommService.projectFullyLoadedNotifier
      .pipe(takeUntil(this.onDestroyNotifier))
      .subscribe(loaded => {
        this.projectCommServiceReady = loaded;
        this.updateScenarioComparisonReadyNotifier();
        if (loaded) {
          this.scenarioComparison = this.projectCommService.getScenarioComparison();
        }
      });

    // when the project save notifier is triggered update the current scenario comparison object
    // and trigger the update notifier
    this.projectCommService.projectSavedNotifier
      .pipe(takeUntil(this.onDestroyNotifier))
      .subscribe(saved => {
        if (saved) {
          this.scenarioComparison = this.projectCommService.getScenarioComparison();
          this.updateNotifier.emit();
        }
      });

    // when the latest results functionality notifier is ready
    // update the loaded boolean and check the service ready state
    this.projectService.latestResultsFunctionalityReadyNotifier
      .pipe(takeUntil(this.onDestroyNotifier))
      .subscribe(ready => {
        this.projectServiceReady = ready;
        this.updateScenarioComparisonReadyNotifier();
      });
  }

  /**
   * Triggers and cleans up onDestroyNotifier emitter.
   */
  ngOnDestroy() {
    this.onDestroyNotifier.emit();
    this.onDestroyNotifier.unsubscribe();
  }

  /**
   * Returns a deep copy list of all current scenarios. If no scenarios are present
   * an empty array is returned.
   */
  getScenarios() {
    if (this.scenarioComparison?.scenarios) {
      const scenariosCopy: Scenario[] = [];

      for (const scenario of this.scenarioComparison.scenarios) {
        scenariosCopy.push(scenario.deepCopy());
      }

      return scenariosCopy;
    } else {
      return [];
    }
  }

  /**
   * Save a scenario to the project, if an index is provided the scenario saved at the index will be updated and saved.
   * If no index is provided it is assumed that a new scenario is being saved. A unique name check is performed before
   * saving the scenario. If an invalid name is found the user will be displayed an error message and a false boolean
   * will be returned.
   * @param scenario is the Scenario that will be saved to the project
   * @param index is the index of the modified scenario. If no index is provided the scenario will be
   * added as a new scenario.
   */
  saveScenario(scenario: Scenario, index?: number): boolean {
    if (this.scenarioComparison.scenarios) {
      if (Utils.nameIsUnique(scenario.name, this.scenarioComparison.scenarios, index)) {
        if (Utils.isNumeric(index) && index > -1 && index < this.scenarioComparison.scenarios.length) {
          this.scenarioComparison.scenarios[index] = scenario;
        } else {
          this.scenarioComparison.scenarios.push(scenario);
        }
        this.saveScenarioComparison();
        this.commService.performScenarioComparisonMetricsSave();
        return true;
      } else { // invalid name, show error toastr to user
        this.toastrService.error(this.translationService.get('SCENARIO_COMPARISON.ENTRY.SCENARIO_EXISTS'));
        return false;
      }
    }
  }

  /**
   * Performs a scenario deletion at the provided index and saves the scenario comparison.
   * @param index
   */
  deleteScenario(index: number) {
    if (this.scenarioComparison?.scenarios) {
      if (index > -1 && index < this.scenarioComparison.scenarios.length) {
        this.scenarioComparison.scenarios.splice(index, 1);
        this.saveScenarioComparison();
        return;
      }
    }
    throw Error(this.translationService.get('SCENARIO_COMPARISON.SERVICE.CANT_REMOVE_SCENARIO'));
  }

  /**
   * Process the provided affected and invalid scenario data. Affected scenarios baseline values are
   * updated based on the affected scenarios map, and the provided invalid scenarios list is used
   * to delete invalid scenarios. A single scenario comparison save is performed at the end.
   * @param affectedScenariosMap is a map of affected scenarios and input.
   * @param invalidScenariosList is a list of invalid scenario names
   */
  processAffectedAndInvalidScenarios(affectedScenariosMap: Map<string, AffectedScenarioInput[]>,
                                     invalidScenariosList: string[]
  ): void {
    if (affectedScenariosMap?.size > 0) {
      this.processAffectedScenarios(affectedScenariosMap, true);
    }
    if (invalidScenariosList?.length > 0) {
      this.processInvalidScenarios(invalidScenariosList, true);
    }

    this.saveScenarioComparison();
  }

  /**
   * Processes the affected scenarios map and updated the corresponding scenarios baseline values
   * based on the provided data.
   * @param affectedScenariosMap is a map of affected scenarios and input data.
   * @param skipSave is a boolean that can be used to skip the scenario comparison save.
   */
  processAffectedScenarios(affectedScenariosMap: Map<string, AffectedScenarioInput[]>, skipSave = false): void {
    for (const [scenarioName, affectedInputs] of affectedScenariosMap) {

      const scenario = this.scenarioComparison.scenarios.find(scenario => scenario.name === scenarioName);

      if (!scenario) throw new Error('error processing affected scenarios, unable to find scenario with name ' + scenarioName);

      for (const affectedInput of affectedInputs) {
        const foundInput = scenario.inputs.find(input => input.id === affectedInput.name);
        if (foundInput) {
          foundInput.baselineValue = affectedInput.updatedValue;
        }
      }
    }

    if (!skipSave) {
      this.saveScenarioComparison();
    }
  }

  /**
   * Process the provided list of invalid scenarios and delete each of the listed scenarios by name.
   * @param scenarioNames is a list of invalid scenario names.
   * @param skipSave is a boolean that can be used to skip the scenario comparison save.
   */
  processInvalidScenarios(scenarioNames: string[], skipSave = false): void {
    if (!this.scenarioComparison?.scenarios) {
      throw new Error('error while deleting scenarios by name');
    }
    for (const scenarioName of scenarioNames) {
      const index = this.scenarioComparison.scenarios.findIndex(
        s => s.name === scenarioName
      );
      if (index < 0) throw new Error('unable to delete scenario by name: - ' + scenarioName);
      this.scenarioComparison.scenarios.splice(index, 1);
    }

    if (skipSave) {
      this.saveScenarioComparison();
    }
  }

  /**
   * Saves the overall scenario comparison object.
   */
  saveScenarioComparison() {
    if (!this.scenarioComparison) return;
    this.projectCommService.saveScenarioComparison(this.scenarioComparison);
  }

  /**
   * Starts the scenario simulation for the current scenario comparison. The call to java is made
   * and the response is process and displayed to the user when received.
   */
  simulateScenarios() {
    if (this.scenarioComparison.scenarios.length > 0) {
      this.snackService
        .openFromComponent(CustomSnackbarComponent,
          {
            data: {
              message: this.translationService.get('SCENARIO_COMPARISON.SERVICE.SIMULATING_SCENARIOS'),
              id: 'project-simulating_snackbar'
            },
            panelClass: ['anessa-snack-bar-container'],
          }
        );
      this.commService.performScenarioSimulation()
        .pipe(take(1))
        .pipe(takeUntil(this.onDestroyNotifier))
        .subscribe((result: string) => {
          if (result !== 'started') {
            // tslint:disable-next-line:no-console
            console.debug(result); // shouldn't get called
          }
        });
    } else {
      this.toastrService.warning(this.translationService.get('SCENARIO_COMPARISON.SERVICE.MISSING_SCENARIO'));
      return false;
    }
  }

  /**
   * Get the project inputs for the latest result state. The scenario comparison uses the project state of the
   * latest results to populate it's input list and values. A list of ScenarioInputs is returned.
   */
  getLatestResultStateScenarioInputs(): ScenarioInput[] {
    const inputs = this.getLatestResultStateProjectInputs();
    return this.convertProjectInputsToScenarioInputs(inputs);
  }

  getLatestResultStateProjectInputs(): ProjectInputItem[] {
    // get material inputs
    const materialInputs = this.projectService.getLatestResultFeedstockInputs();

    // get plant spec inputs
    const plantSpecInputs = this.projectService.getLatestResultPlantSpecInputs();

    // by default inputs will include material and plant spec inputs
    const inputs: ProjectInputItem[] = [...materialInputs, ...plantSpecInputs];

    // if financial is activated in the project get financial inputs
    if (this.projectService.project.projectInfo.useFinancial) {
      const financialInputs = this.projectService.getLatestResultFinancialInputs();
      inputs.push(...financialInputs);
    }
    return inputs;
  }

  /**
   * Get the biogas share data from the latest result state. The scenario comparison uses the project state of the
   * latest results to fetch the biogas share data. A list of BiogasShare items is returned.
   */
  getBiogasShareData(): BiogasShare[] {
    return this.projectService.getLatestResultsBiogasShareData();
  }


  /**
   * Converts a list of project inputs to a list of scenario inputs. Project inputs are a general input
   * type that can contains data the scenario inputs build from.
   * @param projectInputs is a list of ProjectInputs
   * @private
   */
  private convertProjectInputsToScenarioInputs(projectInputs: ProjectInputItem[]) {
    const scenarioInputs: ScenarioInput[] = [];

    for (const input of projectInputs) {
      const scenarioItem = new ScenarioInput();
      scenarioItem.populateFromProjectInput(input);
      scenarioInputs.push((scenarioItem));
    }

    return scenarioInputs;
  }

  /**
   * Checks the state of the services ready booleans and updates the status of the server ready notifier.
   * @private
   */
  private updateScenarioComparisonReadyNotifier() {
    this.serviceReadyNotifier.next(this.projectCommServiceReady && this.projectServiceReady);
  }

}
